import {
  CENTRAL_VALIDATE_ROLE_IDS,
  FLOW_DATA_FIELD_CONFIG,
  LEAD_STAGE_IDS,
  OPS_ROLE_IDS,
  STATUS_LABELS,
} from './constants';

export function getUserRoleIds() {
  try {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]');
    return Array.isArray(roles) ? roles.map((r) => r.id) : [];
  } catch {
    return [];
  }
}

export function hasAnyRole(roleIds, allowed) {
  const normalizedRoleIds = roleIds.map((id) => Number(id));
  return allowed.some((id) => normalizedRoleIds.includes(Number(id)));
}

export function getStatusLabel(status) {
  return STATUS_LABELS[status] || status || '—';
}

export function getStatusBadgeClass(status) {
  const map = {
    SUBMITTED: 'site-lead-referral-badge--submitted',
    SPOC_ASSIGNED: 'site-lead-referral-badge--spoc',
    VALID_LEAD: 'site-lead-referral-badge--valid',
    REJECTED_LEAD: 'site-lead-referral-badge--rejected',
    FINAL_INCENTIVE_PENDING: 'site-lead-referral-badge--pending',
    FINAL_INCENTIVE_PAID: 'site-lead-referral-badge--paid',
    LEGACY_IMPORTED: 'site-lead-referral-badge--legacy',
  };
  return map[status] || 'site-lead-referral-badge--default';
}

export function getValidationIncentiveAmount(incentiveAmounts) {
  const amount = Number(incentiveAmounts?.validation_amount?.amount);
  if (!Number.isNaN(amount) && amount > 0) return amount;
  return 1000;
}

export function getFinalIncentiveAmount(contractValue, incentiveAmounts) {
  const value = Number(contractValue);
  if (Number.isNaN(value)) return null;

  const threshold = Number(incentiveAmounts?.contract_high?.amount);
  const lowTier = Number(incentiveAmounts?.final_tier_low?.amount);
  const highTier = Number(incentiveAmounts?.final_tier_high?.amount);
  const contractThreshold = !Number.isNaN(threshold) && threshold > 0 ? threshold : 100000;
  const lowAmount = !Number.isNaN(lowTier) && lowTier > 0 ? lowTier : 4000;
  const highAmount = !Number.isNaN(highTier) && highTier > 0 ? highTier : 9000;

  return value < contractThreshold ? lowAmount : highAmount;
}

export function formatIncentiveAmount(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

export function canAssignSpoc(row, roleIds) {
  return (
    row?.current_status === 'SUBMITTED' &&
    hasAnyRole(roleIds, OPS_ROLE_IDS)
  );
}

export function canValidate(row, roleIds) {
  return (
    row?.current_status === 'SPOC_ASSIGNED' &&
    !row?.primary_wallet_request_id &&
    hasAnyRole(roleIds, OPS_ROLE_IDS)
  );
}

export function canReject(row, roleIds) {
  return row?.current_status === 'SPOC_ASSIGNED' && hasAnyRole(roleIds, OPS_ROLE_IDS);
}

export function canCentralValidate(row, roleIds) {
  const stage = row?.flow_data?.lead_stage;
  return (
    row?.current_status === 'VALID_LEAD' &&
    LEAD_STAGE_IDS.includes(stage) &&
    hasAnyRole(roleIds, CENTRAL_VALIDATE_ROLE_IDS)
  );
}

export function canSetStage(row, roleIds) {
  return row?.current_status === 'VALID_LEAD' && hasAnyRole(roleIds, OPS_ROLE_IDS);
}

export function getRowActions(row, roleIds, handlers) {
  const actions = [];
  if (canAssignSpoc(row, roleIds)) {
    actions.push({ name: 'Assign SPOC', onClick: () => handlers.onAssignSpoc(row) });
  }
  if (canValidate(row, roleIds)) {
    actions.push({ name: 'Validate', onClick: () => handlers.onValidate(row) });
  }
  if (canReject(row, roleIds)) {
    actions.push({ name: 'Reject', onClick: () => handlers.onReject(row) });
  }
  if (canCentralValidate(row, roleIds)) {
    actions.push({
      name: 'Central Validate',
      onClick: () => handlers.onCentralValidate(row),
    });
  }
  if (canSetStage(row, roleIds)) {
    actions.push({ name: 'Set Stage', onClick: () => handlers.onSetStage(row) });
  }
  actions.push({ name: 'History', onClick: () => handlers.onHistory(row) });
  return actions;
}

export function formatDate(value) {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return value;
  }
}

export function formatDateTime(value) {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return value;
  }
}

export function getRejectReasonLabel(code, reasons = []) {
  const match = reasons.find((r) => r.code === code);
  return match?.label || code || '—';
}

export function formatHistoryRemarks(entry, rejectReasons = []) {
  const parts = [];
  const rejectionCode = entry?.metadata?.rejection_reason;

  if (rejectionCode) {
    parts.push(getRejectReasonLabel(rejectionCode, rejectReasons));
  }

  const remarks = entry?.remarks?.trim();
  if (remarks) {
    parts.push(remarks);
  }

  return parts.length ? parts.join(' — ') : '—';
}

export function parseSpocListResponse(data = []) {
  const spocOptionsByRegion = {};
  const regionOptions = [];

  data.forEach((item) => {
    if (!item?.region) return;
    regionOptions.push({ id: item.region, name: item.region });
    spocOptionsByRegion[item.region] = (item.spocs || [])
      .filter((spoc) => spoc?.email)
      .map((spoc) => ({
        id: spoc.email,
        name: `${spoc.name} (${spoc.email})`,
      }));
  });

  return { regionOptions, spocOptionsByRegion };
}

const formatFlowFieldValue = (raw, config, rejectReasons) => {
  if (config.isRejectionReason) {
    return getRejectReasonLabel(raw, rejectReasons);
  }
  if (config.isUrl && raw) {
    return (
      <a href={raw} target="_blank" rel="noopener noreferrer">
        View
      </a>
    );
  }
  if (config.isDate) {
    return formatDate(raw);
  }
  if (raw == null || raw === '') {
    return '—';
  }
  return String(raw);
};

export function buildFlowDataFields(flow = {}, rejectReasons = []) {
  return FLOW_DATA_FIELD_CONFIG.map((config) => ({
    key: config.key,
    heading: config.heading,
    data: formatFlowFieldValue(flow[config.key], config, rejectReasons),
    plainValue: true,
    fullWidth: config.fullWidth,
  }));
}
